import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { GlobalStoreService } from '../global-store/global-store.service';

import { RequeteModifierCoordonneesMouvement } from '../../api/model/requete-modifier-coordonnees-mouvement.model';
import { ParticulierMouvementModification } from '../../api/model/particulier-mouvement-modification.model';
import { FormulaireCoordonneesMouvementCourrielModel } from '../../models/formulaire/formulaire-coordonnees-mouvement-courriel.model';
import { CourrielMouvementModification } from '../../api/model/courriel-mouvement-modification.model';
import { FormulaireMouvementService } from '@services/formulaire-mouvement/formulaire-mouvement.service';
import { ParticulierMouvement } from '../../api/model/particulier-mouvement.model';
import { FormulaireCoordonneesMouvementTelephoneModel } from '../../models/formulaire/formulaire-coordonnees-mouvement-telephone.model';
import { TelephoneMouvementModification } from '../../api/model/telephone-mouvement-modification.model';
import { FormulaireCoordonneesMouvementAdresseModel } from '../../models/formulaire/formulaire-coordonnees-mouvement-adresse.model';
import { AdresseMouvementModification } from '../../api/model/adresse-mouvement-modification.model';
import { AdressePostdateeMouvementModification } from '../../api/model/adresse-postdatee-mouvement-modification.model';
import { IndicateursMouvementModificationPreferenceContactDGAGEnum } from 'src/app/api/model/indicateurs-mouvement-modification.model';
import { MouvementUtil } from 'src/app/util/mouvement-util';
import { TelephoneTypeLieuEnum } from 'src/app/constantes/telephone-type-lieu.enum';

@Injectable({
  providedIn: 'root',
})
export class RequeteCoordonneesMouvementService {
  membre?: ParticulierMouvement | null = null;

  private readonly formulaireMouvementService = inject(FormulaireMouvementService);
  private readonly globalStoreService = inject(GlobalStoreService);
  constructor() {}

  construireRequeteModifierCourriel(): RequeteModifierCoordonneesMouvement {
    const form: FormGroup<FormulaireCoordonneesMouvementCourrielModel> | null =
      this.formulaireMouvementService.getFormCourriel();
    this.getMembre();
    const courrielModifie = form?.value.courriel ?? '';
    const empreinte = this.membre?.courriel?.empreinte ?? undefined;
    const courriel: CourrielMouvementModification = {
      adresseCourriel: courrielModifie,
      empreinte: empreinte,
    };
    const particulierMouvement: ParticulierMouvementModification = {
      courriel: courriel,
    };
    return {
      particulierMouvement,
    };
  }

  construireRequeteModifierTelephone(): RequeteModifierCoordonneesMouvement {
    const form: FormGroup<FormulaireCoordonneesMouvementTelephoneModel> | null =
      this.formulaireMouvementService.getFormTelephone();
    this.getMembre();
    const empreinte =
      this.membre?.telephones?.find(
        (t) => t.typeLieu === TelephoneTypeLieuEnum.RESIDENCE_PRINCIPALE,
      )?.empreinte ?? undefined;

    // Nettoie le téléphone (enlève espaces et traits d'union)
    const telephone = form?.getRawValue()
      ? form?.getRawValue().numeroTelephone.replaceAll(/[\s-]/g, '')
      : '';

    const telephoneMouvement: TelephoneMouvementModification = {
      numeroTelephone: telephone,
      numeroPoste: form?.value.numeroPoste ? form?.value.numeroPoste : null,
      typeAppareil: form?.value.typeAppareil ?? '',
      typeLieu: TelephoneTypeLieuEnum.RESIDENCE_PRINCIPALE,
      empreinte: empreinte,
    };
    const particulierMouvement: ParticulierMouvementModification = {
      telephones: [telephoneMouvement],
    };
    return {
      particulierMouvement,
    };
  }

  construireRequeteModifierAdresse(): RequeteModifierCoordonneesMouvement {
    const form: FormGroup<FormulaireCoordonneesMouvementAdresseModel> | null =
      this.formulaireMouvementService.getFormAdresse();
    this.getMembre();

    const adresseCourante: AdresseMouvementModification =
      MouvementUtil.toAdresseMouvementModification(
        form,
        this.membre?.adresseCourante?.empreinte ?? undefined,
      );
    const adressePosdatee: AdressePostdateeMouvementModification = {
      empreinte: this.membre?.adressePostdatee?.empreinte ?? undefined,
    };
    const particulierMouvement: ParticulierMouvementModification = {
      adresse: adresseCourante,
      adressePostdatee: adressePosdatee,
      indicateurs: {
        preferenceContactDGAG: this.getPreferenceCommunicationDGAG(form),
      },
    };
    return {
      particulierMouvement,
    };
  }

  private getMembre(): void {
    this.globalStoreService.getCoordonneesMouvement$().subscribe((particulier) => {
      this.membre = particulier;
    });
  }

  private getPreferenceCommunicationDGAG(
    form: FormGroup<FormulaireCoordonneesMouvementAdresseModel> | null,
  ): IndicateursMouvementModificationPreferenceContactDGAGEnum {
    switch (form?.value.preferenceMomentAssurances) {
      case 'am':
        return IndicateursMouvementModificationPreferenceContactDGAGEnum.MATIN;
      case 'pm':
        return IndicateursMouvementModificationPreferenceContactDGAGEnum.APRES_MIDI;
      case 'soir':
        return IndicateursMouvementModificationPreferenceContactDGAGEnum.SOIR;
      case 'aucun':
        return IndicateursMouvementModificationPreferenceContactDGAGEnum.AUCUNE_PREFERENCE;
      default:
        return IndicateursMouvementModificationPreferenceContactDGAGEnum.NE_PAS_TRANSMETTRE_LE_CHANGEMENT;
    }
  }
}
